import React, { useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
export function DoNotSellMyInfoPage() {
  usePageMeta('Do Not Sell My Info | OnlineCollegeGuide', 'Exercise your right to opt out of the sale or sharing of your personal information.');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    state: '',
    note: ''
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Opt-out request submitted:', formData);
    // Send the request to the admin inbox via the visitor's email client.
    const subject = 'California Privacy Opt-Out Request';
    const body = ['A new Do Not Sell or Share My Personal Information request has been submitted:', '', `Full Name: ${formData.fullName}`, `Email: ${formData.email}`, `Phone: ${formData.phone || 'N/A'}`, `State of Residence: ${formData.state}`, `Additional Notes: ${formData.note || 'N/A'}`].join('\n');
    const mailtoUrl = `mailto:admin@media-matchers.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
    setIsSubmitted(true);
  };
  return <main className="bg-white py-10 sm:py-16" data-id="element-1310" data-mp="2yvd18rfh80ev">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-gray-800" data-id="element-1311" data-mp="2yw5xgygt77ir">
        <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-navy mb-8 transition-colors" data-id="element-1312" data-mp="2tw0d83cvcnap">
          <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-1313" data-mp="2pwj675hsni3f" />
          Back to home
        </Link>

        {/* Page header */}
        <header className="border-b border-gray-200 pb-8 mb-12" data-id="element-1314" data-mp="o31pzdfll8x2">
          <h1 className="text-4xl sm:text-5xl font-bold text-navy tracking-tight leading-tight" data-id="element-1315" data-mp="o18tgqyp852s">
            Do Not Sell or Share My Personal Information
          </h1>
        </header>

        {/* Intro */}
        <div className="space-y-5 leading-relaxed text-base mb-12" data-id="element-1316" data-mp="1270qr2kwf13p">
          <p data-id="element-1317" data-mp="1714yvp5whf6d">
            Under the California Consumer Privacy Act (CCPA) and the California
            Privacy Rights Act (CPRA), California residents have the right to
            opt out of the "sale" or "sharing" of their personal information.
          </p>
          <p data-id="element-1318" data-mp="1lwb80l0alu3x">
            <strong data-id="element-1319" data-mp="1lxvzu2v82yy3">What does "sale" or "sharing" mean?</strong> In the context
            of our services, we do not sell your personal information for money.
            However, we may share your information with educational partners to
            fulfill your requests for information about degree programs, or with
            advertising partners to deliver targeted advertisements. Under
            California law, these activities may be considered a "sale" or
            "sharing" of personal information.
          </p>
          <p data-id="element-1320" data-mp="5jw4z6b8hzhp">
            If you would like to exercise your right to opt out of these
            activities, please complete the form below. Alternatively, you may
            email us at{' '}
            <a href="mailto:contact@onlinecollegeguide.io" className="text-navy underline hover:text-crimson" data-id="element-1321" data-mp="3p53eg2nupnjl">
              contact@onlinecollegeguide.io
            </a>{' '}
            with the subject line "California Privacy Opt-Out".
          </p>
          <p data-id="element-1322" data-mp="3ah03sfyv5nog">
            For more details on how we handle your data, please review our{' '}
            <Link to="/privacy-policy" className="text-navy underline hover:text-crimson" data-id="element-1323" data-mp="36gg9rl9l706j">
              Privacy Policy
            </Link>
            .
          </p>
        </div>

        {/* Form Section */}
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 sm:p-10" data-id="element-1324" data-mp="2rppjwirk2b62">
          {isSubmitted ? <div className="text-center py-8" data-id="element-1325" data-mp="2mrfjntqz4kdl">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6" data-id="element-1326" data-mp="2mqmeuybks4y3">
                <CheckCircleIcon className="w-8 h-8 text-green-600" data-id="element-1327" data-mp="30bs3kftybbg0" />
              </div>
              <h2 className="text-2xl font-bold text-navy mb-4" data-id="element-1328" data-mp="35alk6h4jbike">
                Request Received
              </h2>
              <p className="text-gray-600 max-w-md mx-auto" data-id="element-1329" data-mp="3f2iw5q3daek6">
                Thank you. We have received your request to opt out of the sale
                or sharing of your personal information. We will process your
                request within 15 business days.
              </p>
            </div> : <>
              <h2 className="text-2xl font-bold text-navy mb-6" data-id="element-1330" data-mp="3t20nac44y3wp">
                Opt-Out Request Form
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1331" data-mp="1vh5ue3snfpf">
                <div data-id="element-1332" data-mp="1yosuepzq02dp">
                  <label htmlFor="fullName" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1333" data-mp="1ypkmhqu3zhz8">
                    Full Name <span className="text-crimson" data-id="element-1334" data-mp="ksrhcfh128t9">*</span>
                  </label>
                  <input type="text" id="fullName" required value={formData.fullName} onChange={e => setFormData({
                ...formData,
                fullName: e.target.value
              })} className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy" data-id="element-1335" data-mp="g1rih4oclgo4" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1336" data-mp="uokplmnprg58">
                  <div data-id="element-1337" data-mp="pq9hbgd2hdp4">
                    <label htmlFor="email" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1338" data-mp="pr2qenzi0faf">
                      Email Address <span className="text-crimson" data-id="element-1339" data-mp="3ags4uzwahz2l">*</span>
                    </label>
                    <input type="email" id="email" required value={formData.email} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy" data-id="element-1340" data-mp="3bzs4fs3ellot" />
                  </div>
                  <div data-id="element-1341" data-mp="2k82p42pf3dp7">
                    <label htmlFor="phone" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1342" data-mp="2k8ua6jle1t18">
                      Phone Number{' '}
                      <span className="text-gray-500 font-normal" data-id="element-1343" data-mp="2a9vctx62pjtj">
                        (Optional)
                      </span>
                    </label>
                    <input type="tel" id="phone" value={formData.phone} onChange={e => setFormData({
                  ...formData,
                  phone: e.target.value
                })} className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy" data-id="element-1344" data-mp="1vm4qktlg0vpl" />
                  </div>
                </div>

                <div data-id="element-1345" data-mp="29kl87fkug6mi">
                  <label htmlFor="state" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1346" data-mp="2ob27otdx4mgr">
                    State of Residence <span className="text-crimson" data-id="element-1347" data-mp="2jelal9oqqxrj">*</span>
                  </label>
                  <select id="state" required value={formData.state} onChange={e => setFormData({
                ...formData,
                state: e.target.value
              })} className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-navy" data-id="element-1348" data-mp="2xc9xso6a6gzr">
                    <option value="" data-id="element-1349" data-mp="hhf2ul25eshg">Select a state</option>
                    <option value="CA" data-id="element-1350" data-mp="3h069a96f9voy">California</option>
                    <option value="Other" data-id="element-1351" data-mp="3lxpz86chfwwq">Other</option>
                  </select>
                </div>

                <div data-id="element-1352" data-mp="3pztaouz5j1i0">
                  <label htmlFor="note" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1353" data-mp="3pzba5m5gi14a">
                    Additional Notes{' '}
                    <span className="text-gray-500 font-normal" data-id="element-1354" data-mp="2y7c4g82ijjyg">
                      (Optional)
                    </span>
                  </label>
                  <textarea id="note" rows={4} value={formData.note} onChange={e => setFormData({
                ...formData,
                note: e.target.value
              })} className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy resize-none" placeholder="Please provide any additional details regarding your request..." data-id="element-1355" data-mp="327m9niihe6c9" />
                </div>

                <button type="submit" className="w-full inline-flex items-center justify-center h-[52px] px-8 bg-primary hover:bg-primary-hover active:bg-secondary text-white font-semibold rounded-full text-base leading-none shadow-ds transition-colors duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary" data-id="element-1356" data-mp="221aezyub7tew">
                  Submit Opt-Out Request
                </button>
              </form>
            </>}
        </div>
      </div>
    </main>;
}